import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, wordpressApi } from '../api';
import type { Environment, EnvironmentKind, Project, RollbackPolicy } from '../api';
import { slugify } from '../hooks';
import { ErrorNotice } from '../components/ui';
import { Callout, Field, FieldError, Input, OptionCard, Section, Select } from '../components/primitives';
import { OnboardingChoice, OnboardingLayout } from '../components/OnboardingLayout';
import { PlatformChoice } from '../components/wordpress/PlatformChoice';
import { WordPressSitePicker } from '../components/wordpress/WordPressSitePicker';
import type { WordPressTarget } from '../components/wordpress/WordPressSitePicker';
import { GitConnectModal } from '../components/repository/GitConnectModal';
import { GitProviderChoice, ManualRepositoryUrl, ProviderConnectPrompt, RepositoryList } from '../components/repository/RepositorySelector';
import { gitProviderNames, useGitRepositories } from '../components/repository/useGitRepositories';
import { repositoryFields } from '../components/repository/repositoryFields';
import type { RepositorySelection } from '../components/repository/repositoryFields';
import type { GitRepositories } from '../components/repository/useGitRepositories';
import { rollbackPolicyDescription, rollbackPolicyLabel } from '../labels';

// The selection and its create-project fields are shared with the Magento
// wizard (CO-73); re-exported for this page's callers and tests.
export { repositoryFields } from '../components/repository/repositoryFields';
export type { RepositorySelection } from '../components/repository/repositoryFields';

const draftKey = 'commerce-ops.wordpress-onboarding-draft';

interface Draft {
  name: string;
  slug: string;
  environmentName: string;
  environmentKind: EnvironmentKind;
  policy: RollbackPolicy;
}

/** What was typed before leaving for GitHub's App registration, read once. */
function takeDraft(): Partial<Draft> {
  try {
    const raw = window.sessionStorage.getItem(draftKey);
    window.sessionStorage.removeItem(draftKey);
    return raw ? (JSON.parse(raw) as Partial<Draft>) : {};
  } catch {
    return {};
  }
}

type FormErrors = Partial<Record<'name' | 'environmentName' | 'repository' | 'server' | 'root', string>>;

/**
 * CreateWordPressProjectPage onboards an existing WordPress / WooCommerce
 * site: name the project, choose whether its code deploys from Git, reach
 * the hosting account, choose the installation. Connecting the site
 * inspects it straight away, so the project opens with what the server
 * actually runs — nothing is assumed from the platform name.
 *
 * One step, so no stepper: the shared onboarding layout (CO-90) with the
 * Magento wizard's header, footer and validation — checked when "Create
 * project" is pressed, a message under each field that needs attention and
 * the first of them focused. The repository selector is the one Magento
 * onboarding uses (CO-69).
 */
export function CreateWordPressProjectPage() {
  const navigate = useNavigate();
  const ids = useId();
  const [draft] = useState(takeDraft);
  const [name, setName] = useState(draft.name ?? '');
  const [slug, setSlug] = useState(draft.slug ?? '');
  const [environmentName, setEnvironmentName] = useState(draft.environmentName ?? 'Production');
  const [environmentKind, setEnvironmentKind] = useState<EnvironmentKind>(draft.environmentKind ?? 'production');
  const [policy, setPolicy] = useState<RollbackPolicy>(draft.policy ?? 'automatic_code_rollback_when_safe');
  const [target, setTarget] = useState<WordPressTarget>({ serverId: '', rootPath: '', releasesRoot: '' });

  const [selection, setSelection] = useState<RepositorySelection>({ choice: 'connect', manual: false, manualUrl: '', repository: '', branch: '' });
  const update = (patch: Partial<RepositorySelection>) => setSelection((prev) => ({ ...prev, ...patch }));

  const git = useGitRepositories({
    // Nothing is preselected; a selection the fresh list no longer holds
    // (another account, revoked access) is dropped rather than submitted.
    onRepositoriesLoaded: (repositories) =>
      setSelection((prev) => (prev.repository && !repositories.some((r) => r.fullName === prev.repository) ? { ...prev, repository: '', branch: '' } : prev)),
    onBeforeRedirect: () => {
      try {
        window.sessionStorage.setItem(draftKey, JSON.stringify({ name, slug, environmentName, environmentKind, policy } satisfies Draft));
      } catch {
        // Storage unavailable: the details are typed again.
      }
    },
  });

  const [created, setCreated] = useState<{ project: Project; environment: Environment } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [invalidAttempt, setInvalidAttempt] = useState(0);
  const clearError = (key: keyof FormErrors) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const picked = git.repositories.find((r) => r.fullName === selection.repository) ?? null;
  // A picked repository's branch defaults to the first the provider lists
  // (its default branch comes first) — never to an invented name.
  useEffect(() => {
    if (picked && !picked.branches.includes(selection.branch)) {
      setSelection((prev) => ({ ...prev, branch: picked.branches[0] ?? '' }));
    }
  }, [picked, selection.branch]);

  // A message goes once what it asks for has been changed.
  useEffect(() => clearError('repository'), [selection, git.provider, git.isConnected]);
  useEffect(() => {
    clearError('server');
    clearError('root');
  }, [target]);

  const repositoryReady =
    selection.choice === 'none' ||
    (selection.manual ? selection.manualUrl.trim() !== '' : picked !== null && selection.branch !== '' && git.connectionKind !== null);
  const providerName = gitProviderNames[git.provider];

  /** The same rules as before (CO-60, CO-69), now reported per field. */
  function validate(): FormErrors {
    const errs: FormErrors = {};
    if (name.trim() === '') errs.name = 'Name the project.';
    if (!repositoryReady) {
      if (selection.manual) errs.repository = 'Enter the repository URL, or continue without a repository.';
      else if (!git.isConnected) errs.repository = `Connect ${providerName}, or continue without a repository.`;
      else errs.repository = 'Choose a repository, or continue without one.';
    }
    if (environmentName.trim() === '') errs.environmentName = 'Name the environment.';
    if (target.serverId === '') errs.server = 'Choose or connect a server.';
    else if (target.rootPath === '') errs.root = 'Choose the WordPress installation.';
    else if (!target.rootPath.startsWith('/')) errs.root = 'The WordPress directory must be an absolute path.';
    return errs;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setInvalidAttempt((n) => n + 1);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // A retry after a failed site connection reuses the project and
      // environment it already created instead of making a second one.
      let made = created;
      if (!made) {
        const project = await api.createProject({
          name: name.trim(),
          slug: slug.trim() || slugify(name),
          platform: 'wordpress',
          ...repositoryFields(selection, git),
        });
        const environment = await api.createEnvironment(project.id, {
          name: environmentName.trim(),
          slug: slugify(environmentName),
          kind: environmentKind,
          deployment_mode: 'existing_atomic',
          rollback_policy: policy,
        });
        made = { project, environment };
        setCreated(made);
      }
      await wordpressApi.configureSite(made.project.id, made.environment.id, {
        server_id: target.serverId,
        root_path: target.rootPath,
        releases_root: target.releasesRoot || undefined,
      });
      navigate(`/app/projects/${made.project.id}/overview?env=${encodeURIComponent(made.environment.slug)}`);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSubmitting(false);
    }
  }

  return (
    <OnboardingLayout
      breadcrumbs={[{ label: 'Projects', to: '/app/projects' }, { label: 'New project' }]}
      // Outside the page's form: the dialog has forms of its own.
      outside={<GitConnectModal git={git} />}
      title="New WordPress project"
      onSubmit={submit}
      invalidAttempt={invalidAttempt}
      cancel={{ to: '/app/projects' }}
      primary={
        <button type="submit" className="button button--primary" disabled={submitting} data-testid="wp-create">
          {submitting ? 'Connecting and inspecting' : created ? 'Connect the site' : 'Create project'}
        </button>
      }
    >
      <PlatformChoice selected="wordpress" />

      <Section title="Project">
        <div className="form">
          <Field id={`${ids}-name`} label="Name" error={errors.name}>
            <Input
              required
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                clearError('name');
              }}
              placeholder="Acme shop"
              disabled={Boolean(created)}
              data-testid="wp-project-name"
            />
          </Field>
          <Field label="URL slug">
            <Input
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder={slugify(name) || 'acme-shop'}
              disabled={Boolean(created)}
            />
          </Field>
        </div>
      </Section>

      <Section title="Code" testId="wp-repository">
        {created ? (
          <p className="panel__note muted">
            {created.project.repository_url ? (
              <>
                Repository <code>{created.project.repository_url}</code>
              </>
            ) : (
              'No repository'
            )}
          </p>
        ) : (
          <RepositorySection git={git} selection={selection} update={update} ids={ids} error={errors.repository} />
        )}
      </Section>

      <Section title="Environment">
        <div className="form">
          <div className="wp-picker__row">
            <Field id={`${ids}-environment`} label="Name" error={errors.environmentName}>
              <Input
                required
                value={environmentName}
                onChange={(event) => {
                  setEnvironmentName(event.target.value);
                  clearError('environmentName');
                }}
                disabled={Boolean(created)}
              />
            </Field>
            <Field label="Type">
              <Select
                value={environmentKind}
                onChange={(event) => setEnvironmentKind(event.target.value as EnvironmentKind)}
                disabled={Boolean(created)}
              >
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </Select>
            </Field>
          </div>
          <Field label="After a failed deployment" hint={rollbackPolicyDescription[policy]}>
            <Select value={policy} onChange={(event) => setPolicy(event.target.value as RollbackPolicy)} disabled={Boolean(created)}>
              <option value="automatic_code_rollback_when_safe">{rollbackPolicyLabel.automatic_code_rollback_when_safe}</option>
              <option value="manual_recovery">{rollbackPolicyLabel.manual_recovery}</option>
            </Select>
          </Field>
          <p className="panel__note muted">
            Rollback restores code only. The database is never rolled back automatically; a checkpoint is taken before any
            database change.
          </p>
        </div>
      </Section>

      <Section title="WordPress site">
        <WordPressSitePicker value={target} onChange={setTarget} errors={{ server: errors.server, root: errors.root }} />
      </Section>

      {created && error ? (
        <Callout tone="warning">
          Project and environment created; connecting the site failed. Try again, or{' '}
          <Link to={`/app/projects/${created.project.id}/site?env=${encodeURIComponent(created.environment.slug)}`}>open the project</Link>{' '}
          and connect it there.
        </Callout>
      ) : null}
      <ErrorNotice error={error} />
    </OnboardingLayout>
  );
}

/**
 * RepositorySection is the choice between deploying from Git and
 * connecting the site without a repository, and — for the first — the
 * shared repository selector.
 */
function RepositorySection({
  git,
  selection,
  update,
  ids,
  error,
}: {
  git: GitRepositories;
  selection: RepositorySelection;
  update: (patch: Partial<RepositorySelection>) => void;
  ids: string;
  error?: string;
}) {
  const picked = git.repositories.find((r) => r.fullName === selection.repository) ?? null;
  const connectionNote =
    git.connectionKind === 'github_app'
      ? 'Read via your organization’s GitHub App; nothing is stored on the project.'
      : git.connectionKind === 'token'
        ? `Read with your ${gitProviderNames[git.provider]} token, stored encrypted on the project.`
        : null;

  return (
    <div className="wp-picker">
      <OnboardingChoice label="Deploy code from Git?">
        <OptionCard
          title="Connect a repository"
          selected={selection.choice === 'connect'}
          selectedLabel="Selected"
          onSelect={() => update({ choice: 'connect' })}
          testId="wp-repo-connect"
        />
        <OptionCard
          title="Continue without a repository"
          selected={selection.choice === 'none'}
          selectedLabel="Selected"
          onSelect={() => update({ choice: 'none' })}
          testId="wp-repo-none"
        />
      </OnboardingChoice>

      {selection.choice === 'none' ? (
        <p className="panel__note muted" data-testid="wp-repo-none-note">
          Code is not deployed. A repository can be added later.
        </p>
      ) : (
        <>
          <GitProviderChoice git={git} onChange={() => update({ repository: '', branch: '' })} />

          {selection.manual ? (
            <>
              <ManualRepositoryUrl
                git={git}
                inputId={`${ids}-manual-url`}
                value={selection.manualUrl}
                placeholderName="shop"
                error={error}
                onChange={(manualUrl) => update({ manualUrl })}
                onBack={() => update({ manual: false, manualUrl: '', branch: '' })}
                onVerified={(result) => {
                  if (result.verified && result.default_branch && !selection.branch.trim()) update({ branch: result.default_branch });
                }}
              />
              <Field label="Branch" hint="Optional">
                <Input
                  value={selection.branch}
                  onChange={(event) => update({ branch: event.target.value })}
                  placeholder="the repository's default branch"
                  spellCheck={false}
                  autoComplete="off"
                  data-testid="wp-repo-manual-branch"
                />
              </Field>
              <p className="panel__note muted">
                Access is set up under Repositories on the project, unless the organization’s GitHub App can already read it.
              </p>
            </>
          ) : (
            <div className="onboarding-stack" data-onboarding-invalid={error ? 'true' : undefined}>
              {!git.isConnected ? (
                <ProviderConnectPrompt
                  git={git}
                  onManual={() => update({ manual: true, repository: '', branch: '' })}
                  description="The token is stored encrypted on the project, never in your browser."
                />
              ) : (
                <>
                  <RepositoryList
                    git={git}
                    testId="wp-repo-list"
                    selected={selection.repository}
                    onSelect={(r) => update({ repository: r.fullName, branch: r.branches[0] ?? '' })}
                  />
                  <div className="wp-repo__meta">
                    <button type="button" className="button button--ghost button--small" onClick={() => update({ manual: true, repository: '', branch: '' })}>
                      Enter manual URL
                    </button>
                  </div>
                  {picked ? (
                    <div className="wp-repo__picked" data-testid="wp-repo-picked">
                      <div className="wp-picker__row">
                        <div className="field">
                          <span className="field__label">Repository</span>
                          <span className="wp-repo__name">
                            <code>{picked.fullName}</code>
                            <button
                              type="button"
                              className="button button--ghost button--small"
                              onClick={() => update({ repository: '', branch: '' })}
                              aria-label={`Clear the selected repository ${picked.fullName}`}
                              data-testid="wp-repo-clear"
                            >
                              Clear
                            </button>
                          </span>
                        </div>
                        <Field label="Branch">
                          <Select value={selection.branch} onChange={(event) => update({ branch: event.target.value })} data-testid="wp-repo-branch">
                            {picked.branches.length === 0 ? <option value="">No branches listed</option> : null}
                            {picked.branches.map((b) => (
                              <option key={b} value={b}>
                                {b}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>
                      {connectionNote ? <p className="panel__note muted">{connectionNote}</p> : null}
                    </div>
                  ) : null}
                </>
              )}
              <FieldError message={error} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
